/**
 * Token persistence for the web client. Mirrors the iOS KeychainStorage
 * shape (same StoredTokens fields), but stores in localStorage since web
 * doesn't have Keychain.
 *
 * Tradeoff worth knowing: localStorage is reachable by any JS running on
 * the page, including XSS payloads. For a closed-beta paid coaching tool
 * with a small audience, this is fine. Before public launch, consider
 * moving the access token to memory-only and keeping refresh in an
 * httpOnly cookie (requires backend Set-Cookie work).
 */

const STORAGE_KEY = "pt:tokens.v1";

export interface StoredTokens {
  accessToken: string;
  refreshToken: string;
  accessTokenExpiresAt: string; // ISO8601
  refreshTokenExpiresAt: string; // ISO8601
  userId: string;
}

export function loadTokens(): StoredTokens | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredTokens;
    if (
      !parsed?.accessToken ||
      !parsed?.refreshToken ||
      !parsed?.userId
    ) {
      return null;
    }
    return parsed;
  } catch {
    // Corrupt storage — treat as signed-out rather than crashing.
    return null;
  }
}

export function saveTokens(tokens: StoredTokens): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
}

export function clearTokens(): void {
  localStorage.removeItem(STORAGE_KEY);
}

/** True if the access token's exp is in the past (or within 30s).
 *  Triggers a proactive refresh on next API call. */
export function isAccessTokenExpired(tokens: StoredTokens): boolean {
  const expiresAt = new Date(tokens.accessTokenExpiresAt).getTime();
  return Date.now() >= expiresAt - 30_000;
}
